/**
 * Simple Chart Library for ROI Calculator
 * Dark theme optimized version
 */

class SimpleChart {
    constructor(ctx, config) {
        this.ctx = ctx;
        this.config = config;
        this.data = config.data;
        this.canvas = ctx.canvas || ctx;
        this.init();
    }

    init() {
        this.canvas.width = this.canvas.offsetWidth * 2 || 800; // High DPI
        this.canvas.height = this.canvas.offsetHeight * 2 || 400;
        this.canvas.style.width = (this.canvas.width / 2) + 'px';
        this.canvas.style.height = (this.canvas.height / 2) + 'px';
        this.ctx = this.canvas.getContext('2d');
        this.ctx.scale(2, 2); // High DPI scaling
        this.draw();
    }

    update(animation = 'default') {
        this.clear();
        this.draw();
    }

    clear() {
        this.ctx.clearRect(0, 0, this.canvas.width / 2, this.canvas.height / 2);
    }

    draw() {
        if (this.config.type === 'line') {
            this.drawLineChart();
        } else if (this.config.type === 'bar') {
            this.drawBarChart();
        }
    }

    drawLineChart() {
        const { labels, datasets } = this.data;
        if (!labels || !datasets[0] || !datasets[0].data.length) {
            this.drawEmptyState();
            return;
        }

        const padding = 50;
        const chartWidth = (this.canvas.width / 2) - padding * 2;
        const chartHeight = (this.canvas.height / 2) - padding * 2;

        // Find max value across all datasets
        let maxValue = 0;
        datasets.forEach(dataset => {
            const max = Math.max(...dataset.data.filter(val => val !== null && val !== undefined));
            if (max > maxValue) maxValue = max;
        });

        if (maxValue === 0) {
            this.drawEmptyState();
            return;
        }

        const scale = chartHeight / maxValue;

        // Draw grid
        this.drawGrid(padding, chartWidth, chartHeight, maxValue);

        // Draw datasets
        datasets.forEach((dataset, datasetIndex) => {
            this.drawDataset(dataset, labels, padding, chartWidth, chartHeight, scale, datasetIndex);
        });

        // Draw legend
        if (this.config.options?.plugins?.legend?.display) {
            this.drawLegend(datasets, padding, chartWidth);
        }

        // Draw labels
        this.drawXAxisLabels(labels, padding, chartWidth, chartHeight);
    }

    drawDataset(dataset, labels, padding, chartWidth, chartHeight, scale, datasetIndex) {
        const stepX = chartWidth / Math.max(labels.length - 1, 1);

        // Filter out null values
        const validPoints = dataset.data.map((value, index) => ({
            x: padding + index * stepX,
            y: (this.canvas.height / 2) - padding - (value * scale),
            value: value,
            index: index
        })).filter(point => point.value !== null && point.value !== undefined);

        if (validPoints.length === 0) return;

        this.ctx.lineWidth = dataset.borderWidth || 2;
        this.ctx.strokeStyle = dataset.borderColor || '#ff9500';

        // Draw line
        if (validPoints.length > 1) {
            this.ctx.beginPath();

            if (dataset.tension > 0) {
                // Smooth curve
                this.drawSmoothLine(validPoints, dataset.tension);
            } else {
                // Straight lines
                validPoints.forEach((point, index) => {
                    if (index === 0) {
                        this.ctx.moveTo(point.x, point.y);
                    } else {
                        this.ctx.lineTo(point.x, point.y);
                    }
                });
            }

            this.ctx.stroke();

            // Fill area if specified
            if (dataset.fill && dataset.backgroundColor) {
                this.ctx.fillStyle = dataset.backgroundColor;
                this.ctx.lineTo(validPoints[validPoints.length - 1].x, (this.canvas.height / 2) - padding);
                this.ctx.lineTo(validPoints[0].x, (this.canvas.height / 2) - padding);
                this.ctx.closePath();
                this.ctx.fill();
            }
        }

        // Draw points
        validPoints.forEach(point => {
            this.ctx.fillStyle = dataset.pointBackgroundColor || dataset.borderColor || '#ff9500';
            this.ctx.beginPath();
            this.ctx.arc(point.x, point.y, dataset.pointRadius || 4, 0, Math.PI * 2);
            this.ctx.fill();

            // Point border
            if (dataset.pointBorderWidth > 0) {
                this.ctx.strokeStyle = dataset.pointBorderColor || '#fff';
                this.ctx.lineWidth = dataset.pointBorderWidth;
                this.ctx.stroke();
            }
        });

        // Draw dashed line if specified
        if (dataset.borderDash) {
            this.ctx.setLineDash(dataset.borderDash);
        } else {
            this.ctx.setLineDash([]);
        }
    }

    drawSmoothLine(points, tension) {
        if (points.length < 2) return;

        this.ctx.moveTo(points[0].x, points[0].y);

        for (let i = 1; i < points.length; i++) {
            const previous = points[i - 1];
            const current = points[i];

            if (i === 1) {
                this.ctx.quadraticCurveTo(
                    previous.x + (current.x - previous.x) * tension,
                    previous.y,
                    current.x,
                    current.y
                );
            } else {
                const next = points[i + 1] || current;
                const cp1x = previous.x + (current.x - points[i - 2].x) * tension * 0.5;
                const cp1y = previous.y + (current.y - points[i - 2].y) * tension * 0.5;
                const cp2x = current.x - (next.x - previous.x) * tension * 0.5;
                const cp2y = current.y - (next.y - previous.y) * tension * 0.5;

                this.ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, current.x, current.y);
            }
        }
    }

    drawGrid(padding, chartWidth, chartHeight, maxValue) {
        this.ctx.strokeStyle = '#4a4a4a';
        this.ctx.lineWidth = 1;

        // Vertical grid lines
        const gridLines = 6;
        for (let i = 0; i <= gridLines; i++) {
            const x = padding + (chartWidth / gridLines) * i;
            this.ctx.beginPath();
            this.ctx.moveTo(x, padding);
            this.ctx.lineTo(x, padding + chartHeight);
            this.ctx.stroke();
        }

        // Horizontal grid lines
        for (let i = 0; i <= 5; i++) {
            const y = padding + (chartHeight / 5) * i;
            this.ctx.beginPath();
            this.ctx.moveTo(padding, y);
            this.ctx.lineTo(padding + chartWidth, y);
            this.ctx.stroke();

            // Y-axis labels
            const value = maxValue - (maxValue / 5) * i;
            this.ctx.fillStyle = '#a0a0a0';
            this.ctx.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
            this.ctx.textAlign = 'right';
            this.ctx.textBaseline = 'middle';

            let labelText = '';
            if (value >= 1000) {
                labelText = '€' + (value / 1000).toFixed(1) + 'k';
            } else {
                labelText = '€' + Math.round(value);
            }

            this.ctx.fillText(labelText, padding - 10, y);
        }
    }

    drawXAxisLabels(labels, padding, chartWidth, chartHeight) {
        const stepX = chartWidth / Math.max(labels.length - 1, 1);

        this.ctx.fillStyle = '#a0a0a0';
        this.ctx.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'bottom';

        labels.forEach((label, index) => {
            const x = padding + index * stepX;
            const y = padding + chartHeight + 10;
            this.ctx.fillText(label, x, y);
        });
    }

    drawLegend(datasets, padding, chartWidth) {
        const legendY = (this.canvas.height / 2) - 30;
        const legendX = padding + chartWidth / 2;

        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

        let totalWidth = 0;
        const legendItems = datasets.map(dataset => {
            const textWidth = this.ctx.measureText(dataset.label).width;
            totalWidth += textWidth + 40; // 20px for dot + 20px spacing
            return { label: dataset.label, color: dataset.borderColor, width: textWidth };
        });

        let currentX = legendX - totalWidth / 2;

        legendItems.forEach((item, index) => {
            // Draw legend dot
            this.ctx.fillStyle = item.color;
            this.ctx.beginPath();
            this.ctx.arc(currentX + 6, legendY, 4, 0, Math.PI * 2);
            this.ctx.fill();

            // Draw legend text
            this.ctx.fillStyle = '#a0a0a0';
            this.ctx.textAlign = 'left';
            this.ctx.fillText(item.label, currentX + 15, legendY);

            currentX += item.width + 40;
        });
    }

    drawEmptyState() {
        const centerX = this.canvas.width / 4;
        const centerY = this.canvas.height / 4;

        this.ctx.fillStyle = '#6a6a6a';
        this.ctx.font = '16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText('Add products to see chart', centerX, centerY);
    }

    drawBarChart() {
        const { labels, datasets } = this.data;
        if (!labels || !datasets[0] || !datasets[0].data.length) {
            this.drawEmptyState();
            return;
        }

        const padding = 50;
        const chartWidth = (this.canvas.width / 2) - padding * 2;
        const chartHeight = (this.canvas.height / 2) - padding * 2;
        const barWidth = (chartWidth / labels.length) * 0.7;
        const barSpacing = (chartWidth / labels.length) * 0.3;

        const maxValue = Math.max(...datasets[0].data);
        if (maxValue === 0) {
            this.drawEmptyState();
            return;
        }

        const scale = chartHeight / maxValue;

        // Draw grid
        this.drawGrid(padding, chartWidth, chartHeight, maxValue);

        // Draw bars
        datasets[0].data.forEach((value, index) => {
            const x = padding + index * (barWidth + barSpacing) + barSpacing / 2;
            const barHeight = value * scale;
            const y = (this.canvas.height / 2) - padding - barHeight;

            this.ctx.fillStyle = datasets[0].backgroundColor || '#4a90e2';
            this.ctx.fillRect(x, y, barWidth, barHeight);

            // Draw value on top of bar
            if (value > 0) {
                this.ctx.fillStyle = '#ffffff';
                this.ctx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
                this.ctx.textAlign = 'center';
                this.ctx.textBaseline = 'bottom';
                this.ctx.fillText(value.toFixed(1) + '%', x + barWidth / 2, y - 5);
            }
        });

        // Draw X-axis labels
        this.drawXAxisLabels(labels, padding, chartWidth, chartHeight);
    }
}

// Global Chart object for compatibility
window.Chart = SimpleChart;